import { useMemo } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { IconBrandAlfred } from '@components/icons/brand-alfred';
import { IconBrandDV } from '@components/icons/brand-dv';
import { IconBrandTengslatorg } from '@components/icons/brand-tengslatorg';
import { IconBrandTvinna } from '@components/icons/brand-tvinna';
import { IconFacebook } from '@components/icons/facebook';
import { IconLinkedin } from '@components/icons/linkedin';
import { IconTwitter } from '@components/icons/twitter';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { sortByObjectKey } from '@utils/collections';

import fiftyLogo from 'components/icons/logo-green.svg';
import ShowMore from 'components/ShowMore';
import { useStore } from 'models/Provider';
import type { JobStatistics } from 'types/jobs';

import { Line, SourceCard } from './styles';

type Props = {
  statistics: JobStatistics;
};

export const TrafficSources = observer<Props>(function TrafficSources({ statistics }) {
  const { t } = useStore();

  const sources = useMemo(() => {
    return sortByObjectKey(
      Object.keys(statistics.sources.urls).map((key) => ({
        url: key,
        ...statistics.sources.urls[key],
      })),
      'count',
      true,
    );
  }, [statistics.sources.urls]);

  return (
    <Stack space={2}>
      <Text font="semibold14" color="purple100" text={t('job.statistics.traffic.heading')} />
      <Stack space={2}>
        <ShowMore
          max={sources.length}
          count={1}
          inc={100}
          render={({ count }: { count: number }) =>
            sources.slice(0, count).map((source) => {
              return <Source key={source.url} {...source} total={statistics.counts.applicants} />;
            })
          }
        />
        {sources.length === 0 && (
          <Text text={t('job.statistics.traffic.empty')} font="regular16" color="purple60" />
        )}
      </Stack>
    </Stack>
  );
});

function Source({
  url,
  count,
  hired,
  total,
}: {
  url: string;
  count: number;
  hired: number;
  total: number;
}) {
  const { t } = useStore();
  return (
    <SourceCard>
      <Stack orientation="horizontal" space={6} justify="space-between" align="center">
        <Stack orientation="horizontal" space={4} align="center">
          <StyledSource>{getSourceLogo(url)}</StyledSource>
          <Text
            font="bold16"
            color="purple100"
            text={url === 'direct' ? t('job.statistics.overview.direct') : url}
          />
        </Stack>
        <Stack orientation="horizontal" space={6} align="center">
          <Stack space={0} align="center">
            <Stack orientation="horizontal" space={2} justify="center" style={{ width: '6.5rem' }}>
              <Text font="heading2" color="purple100" text={count} />
              <Text font="heading2" color="purple40" text="/" />
              <Text
                font="heading2"
                color="blue100"
                text={count === 0 || total === 0 ? '0%' : `${((count / total) * 100).toFixed(0)}%`}
              />
            </Stack>
            <Text font="regular12" color="purple80" text={t('job.statistics.applicants')} />
          </Stack>
          <Line />
          <Stack space={0} align="center">
            <Stack orientation="horizontal" space={2} justify="center" style={{ width: '6.5rem' }}>
              <Text font="heading2" color="purple100" text={hired} />
              <Text font="heading2" color="purple40" text="/" />
              <Text
                font="heading2"
                color="blue100"
                text={hired === 0 || total === 0 ? '0%' : `${((hired / total) * 100).toFixed(0)}%`}
              />
            </Stack>
            <Text font="regular12" color="purple80" text={t('job.statistics.hired')} />
          </Stack>
        </Stack>
      </Stack>
    </SourceCard>
  );
}

function getSourceLogo(url: string) {
  if (url.includes('50skills.com')) {
    return <img height={32} width={32} src={fiftyLogo} alt="50skills" />;
  }

  if (url.includes('twitter.com')) {
    return <IconTwitter size={32} />;
  }

  if (url.includes('facebook.com')) {
    return <IconFacebook size={32} />;
  }

  if (url.includes('dv.is')) {
    return <IconBrandDV size={32} />;
  }

  if (url.includes('tvinna.is')) {
    return <IconBrandTvinna size={32} />;
  }

  if (url.includes('alfred.is')) {
    return <IconBrandAlfred size={32} />;
  }

  if (url.includes('linkedin.com')) {
    return <IconLinkedin size={32} />;
  }

  if (url.includes('tengslatorg.hi.is')) {
    return <IconBrandTengslatorg size={32} />;
  }

  return (
    <DirectSource>
      <svg
        width="32"
        height="11"
        viewBox="0 0 32 11"
        fill="white"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M7.66619 11L10.2129 0.994995H7.18619L6.05285 7.115L4.70619 0.994995H2.67952L1.33285 7.115L0.186185 0.994995H-2.82715L-0.280481 11H2.54619L3.69285 5.33L4.83952 11H7.66619Z" />
        <path d="M20.3875 11L22.9342 0.994995H19.9075L18.7742 7.115L17.4275 0.994995H15.4009L14.0542 7.115L12.9075 0.994995H9.89421L12.4409 11H15.2675L16.4142 5.33L17.5609 11H20.3875Z" />
        <path d="M33.1089 11L35.6556 0.994995H32.6289L31.4956 7.115L30.1489 0.994995H28.1222L26.7756 7.115L25.6289 0.994995H22.6156L25.1622 11H27.9889L29.1356 5.33L30.2822 11H33.1089Z" />
      </svg>
    </DirectSource>
  );
}

const StyledSource = styled.div({
  height: '2rem',
  width: '2rem',
  flexShrink: 0,
  borderRadius: '0.25rem',
  overflow: 'hidden',
});

const DirectSource = styled(StyledSource)({
  color: theme.color.white100,
  backgroundColor: theme.color.purple100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
